import React, { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ELEMENT_TYPES, isShape } from '../types/elements';

const C4_REGISTRY_KINDS = new Set(['system', 'external-system', 'container', 'component']);

export const useCanvasContextMenu = ({
    elements,
    setElements,
    ctxMenu,
    userModifiedRef,
    onDiagramModified,
    wrappedDeleteSelectedElements,
    projectId,
}) => {
    const navigate = useNavigate();

    const handleUpdateElement = useCallback((id, updates) => {
        const currentElement = elements.find((element) => element.id === id);
        const hasChanges = currentElement && Object.entries(updates || {}).some(([key, value]) => currentElement[key] !== value);
        if (!hasChanges) return;
        setElements(prev => prev.map(el => el.id === id ? { ...el, ...updates } : el));
        userModifiedRef.current = true;
        onDiagramModified?.();
    }, [elements, setElements, userModifiedRef, onDiagramModified]);

    const contextMenuItems = useMemo(() => {
        if (!ctxMenu.elementId) return [];

        const element = elements.find(el => el.id === ctxMenu.elementId);
        if (!element) return [];

        const items = [];

        // Arrow specific configuration
        if (element.type === 'arrow') {
             items.push({
                label: 'Тип линии',
                submenu: [
                  { label: 'Сплошная', action: () => handleUpdateElement(element.id, { dashed: false }) },
                  { label: 'Пунктир', action: () => handleUpdateElement(element.id, { dashed: true }) },
                ]
             });

             items.push({
                label: 'Толщина',
                 render: (item, close) => (
                  <div style={{ padding: '8px 16px', minWidth: '200px' }} onClick={e => e.stopPropagation()}>
                    <div style={{ marginBottom: 4,  fontSize: '12px', color: '#666' }}>Толщина: {element.strokeWidth || 2}px</div>
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={element.strokeWidth || 2}
                      onChange={(e) => handleUpdateElement(element.id, { strokeWidth: parseInt(e.target.value) })}
                      style={{ width: '100%', cursor: 'pointer' }}
                      onMouseDown={e => e.stopPropagation()}
                    />
                  </div>
                )
             });

             items.push({
                label: 'Цвет',
                render: (item, close) => (
                  <div style={{ padding: '8px 16px', minWidth: '200px' }} onClick={e => e.stopPropagation()}>
                    <div style={{ marginBottom: 4, fontSize: '12px', color: '#666' }}>Цвет</div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {['#000000', '#666666', '#E57373', '#F44336', '#2196F3', '#4CAF50', '#FF9800'].map(color => (
                        <div
                          key={color}
                          onClick={() => handleUpdateElement(element.id, { stroke: color })}
                          style={{
                            width: 24,
                            height: 24,
                            backgroundColor: color,
                            border: element.stroke === color ? '2px solid #000' : '1px solid #ccc',
                            borderRadius: 4,
                            cursor: 'pointer'
                          }}
                        />
                      ))}
                    </div>
                  </div>
                )
             });

             items.push({ label: 'divider' });

             items.push({
                label: 'Начало стрелки',
                submenu: [
                  { label: 'Нет', action: () => handleUpdateElement(element.id, { startCap: 'none' }) },
                  { label: 'Стрелка', action: () => handleUpdateElement(element.id, { startCap: 'arrow' }) },
                  { label: 'Круг', action: () => handleUpdateElement(element.id, { startCap: 'circle' }) },
                  { label: 'Ромб', action: () => handleUpdateElement(element.id, { startCap: 'diamond' }) },
                ]
             });

             items.push({
                label: 'Конец стрелки',
                submenu: [
                  { label: 'Нет', action: () => handleUpdateElement(element.id, { endCap: 'none' }) },
                  { label: 'Стрелка', action: () => handleUpdateElement(element.id, { endCap: 'arrow' }) },
                  { label: 'Круг', action: () => handleUpdateElement(element.id, { endCap: 'circle' }) },
                  { label: 'Ромб', action: () => handleUpdateElement(element.id, { endCap: 'diamond' }) },
                ]
             });

             items.push({ label: 'divider' });
        } else if (isShape(element)) {
             // General shape configuration

             // Text Content Editing
             items.push({
                label: 'Текст',
                render: (item, close) => (
                  <div style={{ padding: '8px 16px', minWidth: '200px' }} onClick={e => e.stopPropagation()}>
                    <div style={{ marginBottom: 4, fontSize: '12px', color: '#666' }}>Содержимое</div>
                    <textarea
                        value={element.content || ''}
                        onChange={(e) => handleUpdateElement(element.id, { content: e.target.value })}
                        onKeyDown={(e) => e.stopPropagation()} // Чтобы не срабатывали шорткаты Canvas
                        style={{
                            width: '100%',
                            minHeight: 60,
                            padding: '4px',
                            marginBottom: 8,
                            fontSize: '14px',
                            fontFamily: 'inherit',
                            resize: 'vertical',
                            border: '1px solid #ccc',
                            borderRadius: 4
                        }}
                        autoFocus
                    />

                    <div style={{ marginBottom: 4, fontSize: '12px', color: '#666' }}>Размер шрифта: {element.fontSize || 16}px</div>
                    <input
                      type="range"
                      min="8"
                      max="72"
                      value={Math.min(element.fontSize || 16, 72)} // Защита от огромных значений
                      onChange={(e) => handleUpdateElement(element.id, { fontSize: parseInt(e.target.value) })}
                      style={{ width: '100%', cursor: 'pointer' }}
                      onMouseDown={e => e.stopPropagation()}
                    />
                  </div>
                )
             });

             items.push({ label: 'divider' });

             // Background Color
             items.push({
                label: 'Цвет фона',
                render: (item, close) => (
                  <div style={{ padding: '8px 16px', minWidth: '200px' }} onClick={e => e.stopPropagation()}>
                    <div style={{ marginBottom: 4, fontSize: '12px', color: '#666' }}>Цвет фона</div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 8 }}>
                      {['#ffffff', '#f8f9fa', '#fef3c7', '#dbeafe', '#d1fae5', '#fce7f3', '#fee2e2', '#e5e7eb'].map(color => (
                        <div
                          key={color}
                          onClick={() => handleUpdateElement(element.id, { fill: color })}
                          style={{
                            width: 24,
                            height: 24,
                            backgroundColor: color,
                            border: (element.fill || '#ffffff') === color ? '2px solid #000' : '1px solid #ccc',
                            borderRadius: 4,
                            cursor: 'pointer'
                          }}
                        />
                      ))}
                    </div>
                    <input
                        type="color"
                        value={element.fill || '#ffffff'}
                        onChange={(e) => handleUpdateElement(element.id, { fill: e.target.value })}
                        style={{ width: '100%', height: 30, padding: 0 }}
                     />
                  </div>
                )
             });

             // Border Color
             items.push({
                label: 'Цвет границ',
                render: (item, close) => (
                  <div style={{ padding: '8px 16px', minWidth: '200px' }} onClick={e => e.stopPropagation()}>
                    <div style={{ marginBottom: 4, fontSize: '12px', color: '#666' }}>Цвет границ</div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 8 }}>
                      {['#000000', '#6b7280', '#007ACC', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6'].map(color => (
                        <div
                          key={color}
                          onClick={() => handleUpdateElement(element.id, { stroke: color })}
                          style={{
                            width: 24,
                            height: 24,
                            backgroundColor: color,
                            border: (element.stroke || '#000000') === color ? '2px solid #000' : '1px solid #ccc',
                            borderRadius: 4,
                            cursor: 'pointer'
                          }}
                        />
                      ))}
                    </div>
                    <input
                        type="color"
                        value={element.stroke || '#000000'}
                        onChange={(e) => handleUpdateElement(element.id, { stroke: e.target.value })}
                        style={{ width: '100%', height: 30, padding: 0 }}
                     />
                  </div>
                )
             });

             items.push({ label: 'divider' });

             // Border Width
             items.push({
                label: 'Ширина границ',
                 render: (item, close) => (
                  <div style={{ padding: '8px 16px', minWidth: '200px' }} onClick={e => e.stopPropagation()}>
                    <div style={{ marginBottom: 4,  fontSize: '12px', color: '#666' }}>Толщина: {element.strokeWidth || 1}px</div>
                    <input
                      type="range"
                      min="0"
                      max="10"
                      value={element.strokeWidth || 1}
                      onChange={(e) => handleUpdateElement(element.id, { strokeWidth: parseInt(e.target.value) })}
                      style={{ width: '100%', cursor: 'pointer' }}
                      onMouseDown={e => e.stopPropagation()}
                    />
                  </div>
                )
             });

             // Border Type
             items.push({
                label: 'Тип границ',
                submenu: [
                  { label: 'Сплошная', action: () => handleUpdateElement(element.id, { strokeDasharray: undefined }) },
                  { label: 'Пунктир', action: () => handleUpdateElement(element.id, { strokeDasharray: '5,5' }) },
                  { label: 'Нет границ', action: () => handleUpdateElement(element.id, { strokeWidth: 0 }) },
                ]
             });

             // Corner Radius
             if ([ELEMENT_TYPES.RECTANGLE, ELEMENT_TYPES.ROUNDED_RECT, ELEMENT_TYPES.TERMINATOR, ELEMENT_TYPES.PROCESS].includes(element.type)) {
                 items.push({
                    label: 'Закругление',
                     render: (item, close) => (
                      <div style={{ padding: '8px 16px', minWidth: '200px' }} onClick={e => e.stopPropagation()}>
                        <div style={{ marginBottom: 4,  fontSize: '12px', color: '#666' }}>Радиус: {element.borderRadius || 0}px</div>
                        <input
                          type="range"
                          min="0"
                          max="50"
                          value={element.borderRadius || 0}
                          onChange={(e) => handleUpdateElement(element.id, { borderRadius: parseInt(e.target.value) })}
                          style={{ width: '100%', cursor: 'pointer' }}
                          onMouseDown={e => e.stopPropagation()}
                        />
                      </div>
                    )
                 });
             }

             items.push({ label: 'divider' });
        }

        // C4 element → registry navigation
        if (element.c4?.kind && C4_REGISTRY_KINDS.has(element.c4.kind) && projectId) {
            const systemName = element.defaultLabel
                || elements.find(e => e.type === 'text' && e.attachedToElement === element.id)
                    ?.content?.split('\n')[0]
                || '';
            const label = systemName ? `Открыть «${systemName}» в реестре` : 'Открыть в реестре систем';
            items.push({
                label,
                action: () => navigate(
                `/projects/${projectId}/registry/information_systems`,
                { state: { selectByName: systemName } }
            ),
            });
            items.push({ label: 'divider' });
        }

        // Navigation (System)
        if (element.systemCode || element.parentSystemCode) {
            items.push({
                label: 'Перейти к системе',
                action: () => {
                   const targetSystemId = element.parentSystemId || element.systemCode;
                   const url = `/projects/local/systems?selected=${encodeURIComponent(targetSystemId)}`;
                   window.open(url, '_blank');
                }
            });
             items.push({ label: 'divider' });
        }

        items.push({
            label: 'Удалить',
            action: wrappedDeleteSelectedElements,
            shortcut: 'Del',
            dangerous: true
        });

        return items;
    }, [ctxMenu.elementId, elements, handleUpdateElement, wrappedDeleteSelectedElements, projectId, navigate]);

    return { contextMenuItems, handleUpdateElement };
};
